import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  Select,
  Typography,
  Box,
  ListItemIcon,
  Divider,
  useTheme,
  Modal,
  styled,
  IconButton,
  ListItemText,
  MenuList,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { CaseListFilersOptions } from "@frontend/features/cases/components/CaseListFiltersOptions";
import { SetURLSearchParams } from "react-router-dom";
import { CreateFilterDto, GetFilterByIdDto } from "@bandido/shared/dtos";
import { client } from "../../services";
import { QueryKeys } from "../../types";
import { useQueryClient } from "react-query";
import { useSnackBar } from "../SnackBarContext";
import { useTranslation } from "react-i18next";
import { arrayToPipeDelimited, updateSearchParams } from "../../helper";
import Close from "@mui/icons-material/Close";
import { MoreVert } from "@mui/icons-material";
import { EditCaseListFilers } from "@frontend/features/cases/components/EditCaseListFilters";
import { useSessionFilters } from "@frontend/features/cases/hooks/useSessionFilters";

const StyledBox = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxHeight: `calc(100vh - ${theme.spacing(10)})`,
  background: theme.palette.background.paper,
  'box-shadow': 24,
  paddingTop: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflow: 'auto'
}));

interface Props {
  filtersList: {
    publicFilters: GetFilterByIdDto[];
    privateFilters: GetFilterByIdDto[];
  }
  searchParams: URLSearchParams; // Remove optional - make required
  setSearchParams: SetURLSearchParams;
}

export const FilterSelectorMenu = ({ filtersList, searchParams, setSearchParams }: Props) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const theme = useTheme();
  const { updateFilters, clearFilters } = useSessionFilters(searchParams, setSearchParams);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilter, setSelectedFilter] = useState<GetFilterByIdDto>();
  const [showCreate, setShowCreate] = useState(false);
  const [showPreEdit, setShowPreEdit] = useState(false);
  const [editingFilter, setEditingFilter] = useState<GetFilterByIdDto | undefined>();
  const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [contextMenuFilter, setContextMenuFilter] = useState<GetFilterByIdDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState<GetFilterByIdDto | null>(null);

  const toggleShowCreate = () => setShowCreate(s => !s);
  const toggleShowPreEdit = () => setShowPreEdit(s => !s);

  const { addToast } = useSnackBar();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (filter: GetFilterByIdDto) => {
    if (searchParams && setSearchParams) {

      setSelectedFilter(filter);
      const objCleanParams = {
        search: '',
        status: '',
        teamIds: '',
        workflowIds: '',
        stepIds: '',
        clientIds: '',
        agentIds: '',
        order: '',
      };

      const urlParams = arrayToPipeDelimited(filter.params);

      updateSearchParams(searchParams, setSearchParams, {
        ...objCleanParams,
        ...urlParams
      });
    }

    handleClose();
  };

  const handleCreateFilter = async (values: { name: string; isPublic: boolean }) => {
    try {
      if (searchParams) {
        const params = Object.fromEntries(searchParams.entries());
        const transformedParams = Object.keys(params).reduce((acc, key) => {
          acc[key] = params[key]?.split('|');
          return acc;
        }, {} as Record<string, string[]>);

        const data: CreateFilterDto = {
          ...values,
          params: transformedParams, // Incluimos todos los parámetros transformados.
        };

        await client.post('filters', data);
        queryClient.invalidateQueries([QueryKeys.getCaseSavedFilters]);
        toggleShowCreate();  

        addToast({
          message: t('filters.createdSuccess'),
          color: 'success',
        });
      }
      
    } catch (_e) {
      addToast({
        message: t('filters.createdError'),
        color: 'success',
      });
    }
  };

  const handleUpdateFilter = async (filterId: string, values: { name: string; isPublic: boolean, params: Record<string, unknown> }) => {
    try {
      await client.patch(`filters/${filterId}`, values);
      queryClient.invalidateQueries([QueryKeys.getCaseSavedFilters]);
      setEditingFilter(undefined);
      addToast({
        message: t('filters.createdSuccess'),
        color: 'success',
      });
    } catch (_e) {
      addToast({
        message: t('filters.createdError'),
        color: 'success',
      });
    }
  }

  const handleContextMenuOpen = (event: React.MouseEvent<HTMLElement>, filter: GetFilterByIdDto) => {
    event.stopPropagation();
    setContextMenuAnchorEl(event.currentTarget);
    setContextMenuFilter(filter);
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchorEl(null);
    setContextMenuFilter(null);
  };

  const handleEditFromContext = () => {
    if (contextMenuFilter) {
      setEditingFilter(contextMenuFilter);
      handleContextMenuClose();
    }
  };

  const handleDeleteClick = () => {
    if (contextMenuFilter) {
      setFilterToDelete(contextMenuFilter);
      setDeleteConfirmOpen(true);
      handleContextMenuClose();
    }
  };

  const handleDeleteConfirm = async () => {
    if (filterToDelete) {
      try {
        await client.delete(`filters/${filterToDelete.id}`);
        queryClient.invalidateQueries([QueryKeys.getCaseSavedFilters]);
        addToast({
          message: t('filters.deletedSuccess'),
          color: 'success',
        });
      } catch (_e) {
        addToast({
          message: t('filters.deletedError'),
          color: 'error',
        });
      }
    }
    setDeleteConfirmOpen(false);
    setFilterToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setFilterToDelete(null);
  };

  return (
    <Box sx={{ minWidth: 250 }}>
      <Select
        value={selectedFilter}
        displayEmpty
        onClick={handleOpen}
        size="small"
        variant="outlined"
        fullWidth
        readOnly
        sx={{
          color: theme.palette.disabled,
          ">.MuiSelect-outlined": {
            opacity: 0.5
          },
        }}
      >
        <MenuItem
          value={undefined}
          disabled={true}
        >
          {selectedFilter ? selectedFilter.name : t('filters.filterList')}
        </MenuItem>
      </Select>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem disabled><Typography variant="subtitle2">{t('filters.public')}</Typography></MenuItem>
        {filtersList.publicFilters.map((filter) => (
          <MenuItem key={filter.id} onClick={() => handleSelect(filter)}>
            {filter.name}
          </MenuItem>
        ))}
        <Divider sx={{
          width: '80%',
          ml: '10%'
        }} />
        <MenuItem disabled><Typography variant="subtitle2" >{t('filters.private')}</Typography></MenuItem>
        {filtersList.privateFilters.map((filter) => (
          <MenuItem key={filter.id} onClick={() => handleSelect(filter)}>
            {filter.name}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={toggleShowCreate}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">{t('filters.createFilter')}</Typography>
        </MenuItem>
        <MenuItem onClick={toggleShowPreEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">{t('filters.editFilter')}</Typography>
        </MenuItem>
      </Menu>
      {searchParams && <Modal open={showCreate}>
        <StyledBox>
          <CaseListFilersOptions title={t('filters.createFilter')} searchParams={searchParams} updateFilters={updateFilters} clearFilters={clearFilters} onCreate={handleCreateFilter} onClose={toggleShowCreate} />
        </StyledBox>
      </Modal>}

      {editingFilter && <Modal open={Boolean(editingFilter)}>
        <StyledBox>
          <EditCaseListFilers filter={editingFilter} title="Editar filtro" onClose={() => setEditingFilter(undefined)} onSave={handleUpdateFilter} />
        </StyledBox>

      </Modal>}

      {searchParams && <Modal open={showPreEdit}>
        <StyledBox>
          <Box width="400px" display="flex" flexDirection="column">
            <Box
              display="flex"
              pl={2}
              pr={2}
              pb={1}
              justifyContent="space-between"
              alignItems="center"
              borderBottom="1px solid var(--divider, rgba(0, 0, 0, 0.12))"
            >
              <Typography variant="subtitle2" gutterBottom={false}>
                {t('filters.editFilter')}
              </Typography>
              <IconButton onClick={toggleShowPreEdit}>
                <Close />
              </IconButton>
            </Box>

            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              alignSelf="stretch"
              gap={2}
              padding={theme.spacing(2, 0)}
            >
              <Box>
                <Box pr={2} pl={2}>
                  <Typography variant="subtitle2" color={theme.palette.text.disabled}>{t('filters.private')}</Typography>
                </Box>
                <MenuList sx={{ width: '100%' }}>
                  {filtersList.privateFilters.map((filter) => (
                    <MenuItem key={filter.id}>
                      <ListItemText>{filter.name}</ListItemText>
                      <ListItemIcon>
                        <IconButton size="small" onClick={(e) => handleContextMenuOpen(e, filter)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </ListItemIcon>
                    </MenuItem>
                  ))}
                </MenuList>
              </Box>

              <Box>
                <Box pr={2} pl={2}>
                  <Typography variant="subtitle2" color={theme.palette.text.disabled}>{t('filters.public')}</Typography>
                </Box>
                <MenuList sx={{ width: '100%' }}>
                  {filtersList.publicFilters.map((filter) => (
                    <MenuItem key={filter.id}>
                      <ListItemText>{filter.name}</ListItemText>
                      <ListItemIcon>
                        <IconButton size="small" onClick={(e) => handleContextMenuOpen(e, filter)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </ListItemIcon>
                    </MenuItem>
                  ))}
                </MenuList>
              </Box>

            </Box>
          </Box>
        </StyledBox>
      </Modal>}

      {/* Context Menu para opciones de Edit/Delete */}
      <Menu
        anchorEl={contextMenuAnchorEl}
        open={Boolean(contextMenuAnchorEl)}
        onClose={handleContextMenuClose}
      >
        <MenuItem onClick={handleEditFromContext}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">{t('filters.editFilter')}</Typography>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">{t('filters.deleteFilter')}</Typography>
        </MenuItem>
      </Menu>

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('filters.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('filters.deleteConfirmMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
